import { useCallback, useEffect, useRef, useState } from 'react';

interface UseDragPositionOptions {
    initialStartsIn: number;
    maxStartsIn: number;
    PX_PER_CS: number;
    canDrag: boolean;
    onCommit: (newStartsIn: number) => void;
}

interface UseDragPositionResult {
    startsInCentiInt: number;
    isDragging: boolean;
    handleMouseDown: (e: React.MouseEvent) => void;
    handleTouchStart: (e: React.TouchEvent) => void;
}

// Minimum pixel movement to consider it a drag rather than a click
const DRAG_THRESHOLD = 4;

export function useDragPosition({
    initialStartsIn,
    maxStartsIn,
    PX_PER_CS,
    canDrag,
    onCommit,
}: UseDragPositionOptions): UseDragPositionResult {
    const [startsInCentiInt, setStartsInCentiInt] = useState<number>(Math.abs(initialStartsIn));
    const [isDragging, setIsDragging] = useState<boolean>(false);

    const dragStartX          = useRef<number>(0);
    const startsInAtDragStart = useRef<number>(0);
    const onCommitRef         = useRef(onCommit);

    useEffect(() => {
        onCommitRef.current = onCommit;
    }, [onCommit]);

    useEffect(() => {
        setStartsInCentiInt(Math.abs(initialStartsIn));
    }, [initialStartsIn]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (!canDrag) return;
        // No preventDefault here — lets WaveSurfer receive the click event for seeking
        dragStartX.current = e.clientX;
        startsInAtDragStart.current = startsInCentiInt;
        setIsDragging(true);
    }, [canDrag, startsInCentiInt]);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        if (!canDrag) return;
        dragStartX.current = e.touches[0].clientX;
        startsInAtDragStart.current = startsInCentiInt;
        setIsDragging(true);
    }, [canDrag, startsInCentiInt]);

    // Mouse listeners registered on document while dragging
    useEffect(() => {
        if (!isDragging) return;

        const clamp = (val: number) => Math.round(Math.max(0, Math.min(maxStartsIn, val)));

        const handleMouseMove = (e: MouseEvent) => {
            const deltaX = e.clientX - dragStartX.current;
            if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
            e.preventDefault();
            setStartsInCentiInt(clamp(startsInAtDragStart.current + deltaX * PX_PER_CS));
        };

        const handleMouseUp = (e: MouseEvent) => {
            setIsDragging(false);
            const deltaX = e.clientX - dragStartX.current;
            // Pure click — let WaveSurfer handle seeking, don't update starts_in
            if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
            // Real drag — block the click event that follows so WaveSurfer doesn't seek
            const blockClick = (evt: MouseEvent) => {
                evt.stopPropagation();
                document.removeEventListener('click', blockClick, true);
            };
            document.addEventListener('click', blockClick, true);
            const newStartsIn = clamp(startsInAtDragStart.current + deltaX * PX_PER_CS);
            setStartsInCentiInt(newStartsIn);
            onCommitRef.current(newStartsIn);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, maxStartsIn, PX_PER_CS]);

    // Touch listeners registered on document while dragging
    useEffect(() => {
        if (!isDragging) return;

        const clamp = (val: number) => Math.round(Math.max(0, Math.min(maxStartsIn, val)));

        const handleTouchMove = (e: TouchEvent) => {
            const deltaX = e.touches[0].clientX - dragStartX.current;
            if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
            e.preventDefault();
            setStartsInCentiInt(clamp(startsInAtDragStart.current + deltaX * PX_PER_CS));
        };

        const handleTouchEnd = (e: TouchEvent) => {
            setIsDragging(false);
            const deltaX = e.changedTouches[0].clientX - dragStartX.current;
            if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
            const newStartsIn = clamp(startsInAtDragStart.current + deltaX * PX_PER_CS);
            setStartsInCentiInt(newStartsIn);
            onCommitRef.current(newStartsIn);
        };

        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd);
        return () => {
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isDragging, maxStartsIn, PX_PER_CS]);

    return { startsInCentiInt, isDragging, handleMouseDown, handleTouchStart };
}
